<script setup>
import moment from "moment";
import { onMounted, watch, ref } from "vue";
import { useStore } from "vuex";
import { useUserStore } from "@/_store/userStore";
import { userTableStore } from "@/_store/userTableStore";
import ArgonButton from "@/components/ArgonButton.vue";
import ActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import { argonTableStore } from "@/_store/argonTableStore";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import VehicleAvatar from "@/components/VehicleAvatar.vue";
import CopyValue from "@/components/CopyValue.vue";
import ConfirmModal from "@/components/modal/ConfirmModal.vue";
import { useConfirmModal } from "@/composables/useConfirmModal";
import NewUserModal from "./NewUserModal.vue";
import EditUserModal from "./EditUserModal.vue";
import ReassignVehiclesModal from "./ReassignVehiclesModal.vue";
import { computed } from "vue";
import { errorUtils, fileUtils } from "@/utils";

const store = useStore();
var newUserModal = null;
var editUserModal = null;
const editUserModalRef = ref(null);
var reassignVehiclesModal = null;
const reassignVehiclesModalRef = ref(null);
const bootstrap = store.state.bootstrap;

const snackbar = ref(null);
const closeSnackbar = () => {
  snackbar.value = null;
};
const {
  showConfirmModal,
  confirmTitle,
  confirmText,
  confirmButtonText,
  cancelButtonText,
  confirmButtonClass,
  cancelButtonClass,
  confirmLoading,
  openConfirmModal,
  handleConfirmModal,
} = useConfirmModal();
const userStore = useUserStore();
const userListStore = userTableStore();
const props = defineProps(
  {
    type: {
      type: String,
      required: true
    }
  }
)
const tableStore = argonTableStore();

const isTtmmUserType = computed(() => props.type === "TTMM_APP");
const fieldFilters = computed(() => {
  const filters = [
    {
      field: "user",
      description: "Usuario",
    },
  ];

  if (isTtmmUserType.value) {
    filters.push(
      {
        field: "name",
        description: "Nombre",
      },
      {
        field: "phone",
        description: "Teléfono",
      },
      {
        field: "vehicle.name",
        description: "Vehículo",
      },
      {
        field: "vehicle.model",
        description: "Modelo",
      }
    );
  }

  return filters;
});
const columns = computed(() => {
  const baseColumns = [
    {
      header: "Usuario",
      field: "user",
      sort: false,
    },
  ];

  if (isTtmmUserType.value) {
    baseColumns.push(
      {
        header: "Vehículo",
        field: "vehicle",
        sort: false,
      },
      {
        header: "Nombre",
        field: "name",
        sort: false,
      },
      {
        header: "Teléfono",
        field: "phone",
        sort: false,
      }
    );
  }

  baseColumns.push(
    {
      header: "Creado",
      field: "created_at",
      sort: false,
    },
    {
      header: "Estado",
      field: "status",
      sort: false,
      position: "center",
      width: "w-5",
    },
    {
      header: "",
      field: "actions",
      sort: false,
      position: "center",
      width: "w-1",
    }
  );

  return baseColumns;
});
const defaultFilters = ref([
  {
    field: "status",
    label: "Estado",
    multi: false,
    options: [
      { label: "Activo", value: "ACTIVED" },
      { label: "Inactivo", value: "INACTIVED" },
    ],
  }
]);
onMounted(() => {
  tableStore.clear();
  tableStore.clearPagination();
  const savedFilters = userStore.filtersPage.users?.filters || [];
  tableStore.changeFilters(savedFilters);
  reloadPage();
});

function reloadPage() {
  const query = {
    ...(tableStore.query || {}),
    roles: props.type,
  };

  userStore.filtersPage.users = {
    filters: tableStore.filters,
    filtersFavorite: tableStore.filtersFavorite,
  };
  userListStore.getUsers(
    tableStore.pagination,
    query,
    tableStore.sort
  );
}
function activateUser() {
  userStore.activateUsers(tableStore.checkedList);
}
function deactivateUser() {
  userStore.deactivateUsers(tableStore.checkedList);
}
function forwardPassword() {
  openConfirmModal({
    title:
      "¿Está seguro de restaurar la contraseña de los usuarios seleccionados?",
    text: "¡Si el usuario está activo recibirá un email con su nueva contraseña!",
    confirmButtonText: "Restaurar",
    confirmButtonClass: "btn btn-xs ms-1 bg-gradient-danger",
    onConfirm: () => {
      userStore.forwardPassword(tableStore.checkedList);
    },
  });
}

function activateUserRow(userId) {
  if (!userId) return;
  userStore.activateUsers([userId]);
}

function deactivateUserRow(userId) {
  if (!userId) return;
  userStore.deactivateUsers([userId]);
}

function forwardPasswordRow(userId) {
  if (!userId) return;

  openConfirmModal({
    title: "¿Está seguro de restaurar la contraseña de este usuario?",
    text: "¡Si el usuario está activo recibirá un email con su nueva contraseña!",
    confirmButtonText: "Restaurar",
    confirmButtonClass: "btn btn-xs ms-1 bg-gradient-danger",
    onConfirm: () => {
      userStore.forwardPassword([userId]);
    },
  });
}
function getPosition(field) {
  const column = columns.value.find((col) => col.field === field);
  return column && column.position ? column.position : "start";
}
function newUser() {
  newUserModal = new bootstrap.Modal(document.getElementById("newUserModal"), {
    keyboard: true,
  });
  newUserModal.show();
}

async function reassignVehicles() {
  const allUsers = userListStore.users?.data || [];
  const selectedUsers = allUsers.filter((item) =>
    tableStore.checkedList.includes(item._id)
  );

  await reassignVehiclesModalRef.value?.loadActiveVehicles();
  reassignVehiclesModalRef.value?.setRows(selectedUsers);
  reassignVehiclesModal = new bootstrap.Modal(
    document.getElementById("reassignVehiclesModal"),
    { keyboard: true }
  );
  reassignVehiclesModal.show();
}

async function exportUsers() {
  const query = {
    ...(tableStore.query || {}),
    roles: props.type,
  };
  const bytes = await userListStore.exportUsers(
    tableStore.pagination,
    query,
    tableStore.sort
  );

  fileUtils.downloadBytesFile(bytes, fileUtils.generateDownloadFileName("xlsx"));
}

function getRowActionsMenuId(userItem) {
  return `user-row-actions-${String(userItem?._id || "unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}`;
}

function getRowActions(userItem) {
  const isActive = String(userItem?.status || "").toUpperCase() === "ACTIVED";

  const actions = [
    [
      { label: "Activar", event: "activate", disabled: isActive },
      { label: "Desactivar", event: "deactivate", disabled: !isActive },
    ],
  ];

  if (isTtmmUserType.value) {
    actions.push([
      { label: "Modificar", event: "modify" },
      { label: "Reasignar vehículo", event: "reassignVehicle" },
    ]);
  }

  actions.push([{ label: "Restaurar Contraseña", event: "restorePassword" }]);

  return actions;
}

async function reassignVehicleRow(userItem) {
  await reassignVehiclesModalRef.value?.loadActiveVehicles();
  reassignVehiclesModalRef.value?.setRows([userItem]);
  reassignVehiclesModal = new bootstrap.Modal(
    document.getElementById("reassignVehiclesModal"),
    { keyboard: true }
  );
  reassignVehiclesModal.show();
}

function modifyUserRow(userItem) {
  editUserModalRef.value?.setUser(userItem);
  editUserModal = new bootstrap.Modal(document.getElementById("editUserModal"), {
    keyboard: true,
  });
  editUserModal.show();
}

function onRowAction(userItem, action) {
  const actionEvent = action?.event;
  const userId = userItem?._id;
  if (!actionEvent || !userId) return;

  if (actionEvent === "modify") {
    modifyUserRow(userItem);
    return;
  }

  if (actionEvent === "activate") {
    activateUserRow(userId);
    return;
  }

  if (actionEvent === "deactivate") {
    deactivateUserRow(userId);
    return;
  }

  if (actionEvent === "restorePassword") {
    forwardPasswordRow(userId);
    return;
  }

  if (actionEvent === "reassignVehicle") {
    reassignVehicleRow(userItem);
  }
}
watch(
  () => userListStore.error,
  (value) => {
    if (value) {
      snackbar.value = {
        title: "Error",
        message: value.message,
        type: "danger",
        color: "white",
      };
    }
  }
);
watch(
  () => userStore.newedUser,
  (value) => {
    if (value) {
      newUserModal.hide();
      reloadPage();
    }
  }
);
watch(
  () => userStore.deactivatedUsers,
  (value) => {
    if (value) {
      tableStore.clearSelection();
      reloadPage();
      snackbar.value = {
        title: "OK",
        message: "Los usuarios seleccionados se han desactivado",
        type: "info",
        color: "white",
      };
    }
  }
);
watch(
  () => userStore.activatedUsers,
  (value) => {
    if (value) {
      tableStore.clearSelection();
      reloadPage();
      snackbar.value = {
        title: "OK",
        message: "Los usuarios seleccionados se han activado",
        type: "info",
        color: "white",
      };
    }
  }
);
watch(
  () => userStore.forwadedPassword,
  (value) => {
    if (value) {
      tableStore.clearSelection();
      reloadPage();
      snackbar.value = {
        title: "OK",
        message:
          "Enviado email con nueva contraseña a los usuarios seleccionados",
        type: "info",
        color: "white",
      };
    }
  }
);

watch(
  () => userStore.reassignedVehicles,
  (value) => {
    if (value) {
      reassignVehiclesModal.hide();
      tableStore.clearSelection();
      reloadPage();
      snackbar.value = {
        title: "OK",
        message: "Vehículos reasignados correctamente",
        type: "info",
        color: "white",
      };
    }
  }
);
watch(
  () => userStore.modifiedUser,
  (value) => {
    if (value) {
      editUserModal.hide();
      reloadPage();
      snackbar.value = {
        title: "OK",
        message: "Usuario modificado correctamente",
        type: "info",
        color: "white",
      };
    }
  }
);
watch(
  () => userStore.error,
  (value) => {
    if (value) {
      snackbar.value = {
        title: "Error",
        message:  errorUtils.getErrorMessageHtml(value),
        type: "danger",
        color: "white",
      };
    }
  }
);
</script>
<template>
  <div class="card users-table-view">
    <div class="card-body users-table-body">
      <argon-table
        v-if="userListStore.users"
        :show-config-option="true"
        :show-importar="false"
        :columns="columns"
        :field-filters="fieldFilters"
        :items="userListStore.users"
        :table-hover="false"
        :default-filters="defaultFilters"
        @page-changed="reloadPage"
        @on-reload="reloadPage"
        @filter-changed="reloadPage"
        @new-item="newUser"
        @export-data="exportUsers"
      >
        <template #buttons>
          <div
            class="dropdown d-inline"
            data-dropdown-static
            style="--dropdown-gap: 4px"
          >
            <argon-button
              id="navbarDropdownPrinter"
              color="primary"
              variant="outline"
              class="dropdown-toggle me-2 btn-xs"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
            >
              <i class="fa fa-print me-sm-2"></i>Acciones
            </argon-button>
            <ul
              class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
              aria-labelledby="navbarDropdownPrinter"
              style
            >
              <li>
                <a
                  class="dropdown-item border-radius-md"
                  href="javascript:;"
                  @click="activateUser"
                >
                  Activar
                </a>
              </li>
              <li>
                <a
                  class="dropdown-item border-radius-md"
                  href="javascript:;"
                  @click="deactivateUser"
                >
                  Desactivar
                </a>
              </li>
              <template v-if="isTtmmUserType">
                <li>
                  <hr class="dropdown-divider" />
                </li>
                <li>
                  <a
                    class="dropdown-item border-radius-md"
                    href="javascript:;"
                    @click="reassignVehicles"
                  >
                    Reasignar vehículos
                  </a>
                </li>
              </template>
              <li>
                <hr class="dropdown-divider" />
              </li>
              <li>
                <a
                  class="dropdown-item border-radius-md"
                  href="javascript:;"
                  @click="forwardPassword"
                >
                  Restaurar Contraseña
                </a>
              </li>
            </ul>
          </div>
        </template>
        <template #default="{ item }">
          <argon-td
            :position="getPosition('user')"
            :text-primary="item.user"
          ></argon-td>
          <argon-td
            v-if="isTtmmUserType"
            :position="getPosition('vehicle')"
            :text-primary="item.vehicle?.name || ''"
            :text-secondary="item.vehicle?.model || ''"
          >
            <template #prepend>
              <vehicle-avatar
                :label="item.vehicle?.name || ''"
                :abbreviation="item.vehicle?.abbreviation || ''"
                :accent-color="item.vehicle?.route_color_hex || '#5baee9'"
                :size="28"
              />
            </template>
          </argon-td>
          <argon-td
            v-if="isTtmmUserType"
            :position="getPosition('name')"
            :text-primary="item.name || ''"
          ></argon-td>
          <argon-td
            v-if="isTtmmUserType"
            :position="getPosition('phone')"
            :text-primary="item.phone || ''"
          >
            <template v-if="item.phone" #append>
              <copy-value :value-to-copy="String(item.phone)" size="xs" show-on-hover />
            </template>
          </argon-td>
          <argon-td
            :position="getPosition('created_at')"
            :text-primary="moment.utc(item.created_at).local().format('DD/MM/YYYY HH:mm')"
            :text-secondary="moment.utc(item.created_at).local().fromNow()"
          ></argon-td>
          <argon-td :position="getPosition('status')">
            <argon-status
              entity="user"
              :status="item.status || 'INACTIVED'"
            
            />
          </argon-td>
          <argon-td :position="getPosition('actions')">
            <div class="table-row-actions">
              <actions-dropdown
                :menu-id="getRowActionsMenuId(item)"
                :actions="getRowActions(item)"
                aria-label="Acciones del usuario"
                trigger-class="table-action-button"
                menu-class="table-action-menu-dropdown"
                @action="(action) => onRowAction(item, action)"
              />
            </div>
          </argon-td>
        </template>
      </argon-table>
    </div>
    <NewUserModal id="newUserModal" :type="props.type"></NewUserModal>
    <EditUserModal
      v-if="isTtmmUserType"
      id="editUserModal"
      ref="editUserModalRef"
    ></EditUserModal>
    <ReassignVehiclesModal
      v-if="isTtmmUserType"
      id="reassignVehiclesModal"
      ref="reassignVehiclesModalRef"
    ></ReassignVehiclesModal>
  </div>
  <confirm-modal
    v-if="showConfirmModal"
    v-model="showConfirmModal"
    :title="confirmTitle"
    :text="confirmText"
    :confirm-button-text="confirmButtonText"
    :cancel-button-text="cancelButtonText"
    :confirm-button-class="confirmButtonClass"
    :cancel-button-class="cancelButtonClass"
    :loading="confirmLoading"
    @confirm="handleConfirmModal"
  />
  <div class="position-fixed top-1 end-1 z-index-2">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped>

:deep(thead th.text-center.w-1:last-child),
:deep(tbody td.text-center:last-child) {
  max-width: 48px;
  min-width: 48px;
  width: 48px;
}
</style>
